// hooks/useProfile.js
//
// Standardized React hook for accessing the unified user profile across every
// screen. Internally wraps ProfileManagementService, which itself maintains
// bidirectional sync between the API, AsyncStorage (under PROFILE_KEYS), and
// per-screen caches.
//
// Usage:
//
//   import useProfile from '../../hooks/useProfile';
//
//   function MyScreen() {
//     const profileCtx = useProfile();
//     if (profileCtx.loading) return <ActivityIndicator />;
//     return <Text>Welcome back, {profileCtx.fullName}</Text>;
//   }
//
// Common fields exposed:
//   profile         — raw r.data object (everything the service returns)
//   loading         — true while bootstrap+getProfile is in flight
//   error           — non-null if the load failed
//   refresh()       — re-fetch from the service (returns a Promise)
//   firstName, lastName, fullName
//   email, photo, phone, userId
//   skills, experiences, educations, certifications, languages
//   completeness    — number 0..100 if the API returns it
//
// Pass `{ autoLoad: false }` to defer the initial load (useful for screens
// that gate loading behind a different lifecycle, e.g. focus events).

import { useState, useEffect, useCallback, useRef } from 'react';
import ProfileManagementService from '../services/ProfileManagementService';

export function useProfile(opts) {
  const { autoLoad = true } = opts || {};
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(autoLoad);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      if (mountedRef.current) setLoading(true);
      await ProfileManagementService.bootstrap();
      const r = await ProfileManagementService.getProfile();
      if (!mountedRef.current) return null;
      if (r && r.success && r.data) {
        setProfile(r.data);
        setError(null);
        return r.data;
      }
      setProfile(null);
      return null;
    } catch (e) {
      if (mountedRef.current) {
        setError(e);
        setProfile(null);
      }
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (autoLoad) refresh();
    return () => { mountedRef.current = false; };
  }, [autoLoad, refresh]);

  // Convenience accessors — defensive against varying server shapes.
  const personal      = (profile && profile.personal) || {};
  const firstName     = personal.first_name || (profile && profile.first_name) || '';
  const lastName      = personal.last_name  || (profile && profile.last_name)  || '';
  const fullName      = `${firstName} ${lastName}`.trim()
                          || (profile && profile.full_name)
                          || (profile && profile.name)
                          || (profile && profile.headline)
                          || '';
  const email         = personal.email || (profile && profile.email) || '';
  const phone         = personal.phone || (profile && profile.phone) || '';
  const photo         = personal.photo
                          || (profile && profile.photo)
                          || (profile && profile.profile_photo)
                          || (profile && profile.avatar)
                          || null;
  const userId        = (profile && (profile.user_id || profile.id)) || personal.user_id || null;
  const headline      = (profile && profile.headline) || personal.headline || '';
  const completeness  = (profile && profile.completeness) || 0;

  return {
    profile,
    loading,
    error,
    refresh,
    // Convenience scalars
    firstName, lastName, fullName, email, phone, photo, userId, headline, completeness,
    // Convenience arrays — always arrays so callers don't need `?? []`
    skills:         (profile && Array.isArray(profile.skills))         ? profile.skills         : [],
    experiences:    (profile && Array.isArray(profile.experiences))    ? profile.experiences    : [],
    educations:     (profile && Array.isArray(profile.educations))     ? profile.educations     : [],
    certifications: (profile && Array.isArray(profile.certifications)) ? profile.certifications : [],
    languages:      (profile && Array.isArray(profile.languages))      ? profile.languages      : [],
  };
}

export default useProfile;
